import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Coins, Trophy, Target, Zap, ShieldCheck, Lock,
  CheckCircle2, ArrowRight, Sparkles, TrendingUp, Clock,
  Gift, Star, Crown, Medal, Percent
} from 'lucide-react';
import { safecoinAPI } from '@/lib/api';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { toast } from 'sonner';

const MISSION_ICONS = {
  kyc_approved: ShieldCheck,
  '2fa_activated': Lock,
  first_deposit: Coins,
  referral: Gift,
};

export default function SafeCoinPage() {
  const { t } = useLanguage();
  const [balance, setBalance] = useState(null);
  const [missions, setMissions] = useState([]);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [spendOptions, setSpendOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spendDialog, setSpendDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [spending, setSpending] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [balRes, missRes, histRes, lbRes, spRes] = await Promise.all([
        safecoinAPI.getBalance(),
        safecoinAPI.getMissions(),
        safecoinAPI.getHistory({ limit: 20 }),
        safecoinAPI.getLeaderboard(),
        safecoinAPI.getSpendOptions(),
      ]);
      setBalance(balRes.data);
      setMissions(missRes.data.missions || []);
      setHistory(histRes.data.history || []);
      setLeaderboard(lbRes.data);
      setSpendOptions(spRes.data);
    } catch (err) {
      console.error('SafeCoin load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSpend = async () => {
    if (selectedTier === null) return;
    setSpending(true);
    try {
      const res = await safecoinAPI.spend({ tier_index: selectedTier });
      toast.success(res.data.message);
      setSpendDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    } finally {
      setSpending(false);
    }
  };

  const RANK_STYLES = [
    'from-yellow-500/20 to-amber-600/10 border-yellow-500/30',
    'from-gray-300/20 to-gray-400/10 border-gray-400/30',
    'from-orange-500/20 to-orange-600/10 border-orange-500/30',
  ];

  if (loading) {
    return (
      <DashboardLayout title="SafeCoin">
        <div className="space-y-6">
          <Skeleton className="h-40 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-48" /><Skeleton className="h-48" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="SafeCoin">
      <div className="space-y-6" data-testid="safecoin-page">
        {/* Balance Hero */}
        <Card className="relative overflow-hidden border-yellow-500/20 bg-gradient-to-br from-yellow-500/5 via-[hsl(var(--card))] to-amber-600/5">
          <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">SafeCoin Bakiyeniz</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
                    <Coins className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="text-4xl font-bold font-['Space_Grotesk'] text-yellow-500" data-testid="safecoin-balance-value">
                      {balance?.balance || 0}
                    </span>
                    <span className="text-lg ml-2 text-[hsl(var(--muted-foreground))]">SC</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="text-center px-4 py-2 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Toplam Kazanilan</p>
                  <p className="text-lg font-semibold text-green-400">{balance?.total_earned || 0}</p>
                </div>
                <div className="text-center px-4 py-2 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Toplam Harcanan</p>
                  <p className="text-lg font-semibold text-orange-400">{balance?.total_spent || 0}</p>
                </div>
                {balance?.active_discount_percent > 0 && (
                  <div className="text-center px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-xs text-green-400">Aktif Indirim</p>
                    <p className="text-lg font-semibold text-green-400">%{balance.active_discount_percent}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="missions" className="w-full">
          <TabsList className="w-full justify-start bg-[hsl(var(--secondary))] p-1">
            <TabsTrigger value="missions" data-testid="tab-missions" className="gap-2">
              <Target className="w-4 h-4" /> Gorevler
            </TabsTrigger>
            <TabsTrigger value="spend" data-testid="tab-spend" className="gap-2">
              <Sparkles className="w-4 h-4" /> Harca
            </TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard" className="gap-2">
              <Trophy className="w-4 h-4" /> Liderlik
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="gap-2">
              <Clock className="w-4 h-4" /> Gecmis
            </TabsTrigger>
          </TabsList>

          {/* Missions Tab */}
          <TabsContent value="missions">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {missions.map((m) => {
                const Icon = MISSION_ICONS[m.key] || Target;
                return (
                  <Card key={m.key} data-testid={`mission-${m.key}`}
                    className={`transition-all duration-300 ${m.completed ? 'border-green-500/30 bg-green-500/5' : 'border-[hsl(var(--border))] hover:border-yellow-500/30'}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.completed ? 'bg-green-500/20' : 'bg-yellow-500/10'}`}>
                            <Icon className={`w-5 h-5 ${m.completed ? 'text-green-400' : 'text-yellow-500'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm">{m.title}</h3>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{m.description}</p>
                          </div>
                        </div>
                        <Badge className={m.completed
                          ? 'bg-green-500/10 text-green-400 border-green-500/20'
                          : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                        }>
                          {m.completed ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Coins className="w-3 h-3 mr-1" />}
                          {m.completed ? 'Tamamlandi' : `+${m.reward} SC`}
                        </Badge>
                      </div>
                      {!m.completed && (
                        <div className="mt-3">
                          <Progress value={m.eligible ? 100 : 0} className="h-1.5" />
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                            {m.eligible ? 'Tamamlanmaya hazir!' : 'Henuz tamamlanmadi'}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Spend Tab */}
          <TabsContent value="spend">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {spendOptions?.tiers?.map((tier) => (
                <Card key={tier.index} data-testid={`spend-tier-${tier.label.toLowerCase()}`}
                  className={`transition-all duration-300 hover:scale-[1.02] cursor-pointer ${
                    tier.index === 0 ? 'border-orange-500/30' :
                    tier.index === 1 ? 'border-gray-400/30' :
                    'border-yellow-500/30'
                  }`}
                  onClick={() => { setSelectedTier(tier.index); setSpendDialog(true); }}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                      tier.index === 0 ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/10' :
                      tier.index === 1 ? 'bg-gradient-to-br from-gray-300/20 to-gray-500/10' :
                      'bg-gradient-to-br from-yellow-500/20 to-amber-600/10'
                    }`}>
                      {tier.index === 0 ? <Medal className="w-8 h-8 text-orange-400" /> :
                       tier.index === 1 ? <Star className="w-8 h-8 text-gray-300" /> :
                       <Crown className="w-8 h-8 text-yellow-500" />}
                    </div>
                    <h3 className="text-xl font-bold font-['Space_Grotesk']">{tier.label}</h3>
                    <div className="my-3">
                      <span className="text-3xl font-bold text-yellow-500">%{tier.discount_percent}</span>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Fee Indirimi</p>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                      <Coins className="w-4 h-4 text-yellow-500" />
                      <span className="font-semibold">{tier.cost} SC</span>
                      <span>/ {tier.duration_days} gun</span>
                    </div>
                    <Button
                      className={`w-full mt-4 ${tier.can_afford ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : ''}`}
                      variant={tier.can_afford ? 'default' : 'secondary'}
                      disabled={!tier.can_afford}
                    >
                      {tier.can_afford ? 'Satin Al' : 'Yetersiz SC'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            {spendOptions?.active_discount > 0 && (
              <Card className="mt-4 border-green-500/20 bg-green-500/5">
                <CardContent className="p-4 flex items-center gap-3">
                  <Percent className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="font-medium text-green-400">Aktif Indirim: %{spendOptions.active_discount}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      Bitis: {spendOptions.discount_expires_at ? new Date(spendOptions.discount_expires_at).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Haftalik Liderlik Tablosu
                </CardTitle>
                <CardDescription>En cok SafeCoin'e sahip ilk 10 kullanici haftalik bonus kazanir</CardDescription>
              </CardHeader>
              <CardContent>
                {leaderboard?.my_rank && (
                  <div className="mb-4 p-3 rounded-lg bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/20">
                    <p className="text-sm">
                      Siralamandz: <span className="font-bold text-[hsl(var(--primary))]">#{leaderboard.my_rank}</span>
                      <span className="mx-2 text-[hsl(var(--muted-foreground))]">|</span>
                      Bakiyen: <span className="font-bold text-yellow-500">{leaderboard.my_balance} SC</span>
                    </p>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Sira</TableHead>
                      <TableHead>Kullanici</TableHead>
                      <TableHead className="text-right">Bakiye</TableHead>
                      <TableHead className="text-right">Bonus</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboard?.leaderboard?.map((entry) => (
                      <TableRow key={entry.rank} className={entry.is_me ? 'bg-[hsl(var(--primary))]/5' : ''}>
                        <TableCell>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            entry.rank === 1 ? 'bg-yellow-500/20 text-yellow-500' :
                            entry.rank === 2 ? 'bg-gray-400/20 text-gray-300' :
                            entry.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                            'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
                          }`}>
                            {entry.rank <= 3 ? ['', <Crown key="1" className="w-4 h-4" />, <Star key="2" className="w-4 h-4" />, <Medal key="3" className="w-4 h-4" />][entry.rank] : entry.rank}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {entry.name} {entry.is_me && <Badge className="ml-2 text-[10px] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">Sen</Badge>}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-yellow-500">{entry.balance} SC</TableCell>
                        <TableCell className="text-right">
                          {entry.bonus > 0 ? (
                            <Badge className="bg-green-500/10 text-green-400 border-green-500/20">+{entry.bonus} SC</Badge>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!leaderboard?.leaderboard || leaderboard.leaderboard.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-[hsl(var(--muted-foreground))] py-8">
                          Henuz liderlik tablosu verisi yok
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>SafeCoin Gecmisi</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Tur</TableHead>
                      <TableHead>Aciklama</TableHead>
                      <TableHead className="text-right">Miktar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">
                          {new Date(entry.created_at).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {entry.entry_type === 'mission' ? 'Gorev' :
                             entry.entry_type === 'trade_mining' ? 'Trade' :
                             entry.entry_type === 'admin_grant' ? 'Admin' :
                             entry.entry_type === 'leaderboard_bonus' ? 'Liderlik' :
                             entry.entry_type === 'spend' ? 'Harcama' :
                             entry.entry_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{entry.description}</TableCell>
                        <TableCell className={`text-right font-semibold ${entry.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {entry.amount > 0 ? '+' : ''}{entry.amount} SC
                        </TableCell>
                      </TableRow>
                    ))}
                    {history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-[hsl(var(--muted-foreground))] py-8">
                          Henuz SafeCoin islemi yok
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Spend Confirmation Dialog */}
        <Dialog open={spendDialog} onOpenChange={setSpendDialog}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <DialogHeader>
              <DialogTitle>SafeCoin Harcama Onayi</DialogTitle>
              <DialogDescription>
                {selectedTier !== null && spendOptions?.tiers?.[selectedTier] && (
                  <>
                    <span className="font-semibold">{spendOptions.tiers[selectedTier].label}</span> seviyesi fee indirimi aktiflestirilecek.
                    <br /><br />
                    Maliyet: <span className="font-bold text-yellow-500">{spendOptions.tiers[selectedTier].cost} SC</span>
                    <br />
                    Indirim: <span className="font-bold text-green-400">%{spendOptions.tiers[selectedTier].discount_percent}</span> ({spendOptions.tiers[selectedTier].duration_days} gun)
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSpendDialog(false)}>Iptal</Button>
              <Button
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
                onClick={handleSpend}
                disabled={spending}
              >
                {spending ? 'Isleniyor...' : 'Onayla'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
